import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, ViewGridIcon, ThumbUpIcon, ChatIcon, EyeIcon } from '@heroicons/react/solid';
import { useAuth } from '../context/AuthContext';
import { fetchIdeas } from '../services/ideaService';

const IdeaCard = ({ idea }) => {
  const [isRead, setIsRead] = useState(false);

  useEffect(() => {
    // Check if the idea has been read
    const readIdeas = JSON.parse(localStorage.getItem('readIdeas') || '[]');
    setIsRead(readIdeas.includes(idea._id));
  }, [idea._id]);

  const markAsRead = () => {
    // Get existing read ideas
    const readIdeas = JSON.parse(localStorage.getItem('readIdeas') || '[]');
    
    // Add idea to read list if not already present
    if (!readIdeas.includes(idea._id)) {
      const updatedReadIdeas = [...readIdeas, idea._id];
      localStorage.setItem('readIdeas', JSON.stringify(updatedReadIdeas));
      setIsRead(true);
    }
  };

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`bg-white overflow-hidden shadow rounded-lg p-4 ${
        !isRead ? 'border-2 border-brand-200' : ''
      }`}
    >
      <div className="flex flex-col">
        {/* Title and Author */}
        <div className="flex justify-between items-start mb-2">
          <Link 
            to={`/idea/${idea._id}`} 
            onClick={markAsRead}
            className={`text-lg font-semibold ${!isRead ? 'text-brand-700' : 'text-gray-900'} hover:underline`}
          >
            {idea.title}
          </Link>
          {idea.isAnonymous ? (
            <span className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded-full">
              Anonymous
            </span>
          ) : (
            <span className="text-sm text-gray-600">
              {idea.author?.name || 'Unknown Author'}
            </span>
          )}
        </div>

        {/* Content */}
        <p className={`text-sm mb-4 line-clamp-3 ${!isRead ? 'text-brand-600' : 'text-gray-500'}`}>
          {idea.content}
        </p>

        {/* Footer with Votes, Comments, and View Details */}
        <div className="flex justify-between items-center">
          {/* Metrics */}
          <div className="flex items-center space-x-3 text-gray-600">
            <div className="flex items-center space-x-1">
              <ThumbUpIcon className="h-4 w-4 text-blue-500" />
              <span className="text-sm">{idea.upvoteCount || 0} </span>
            </div>
            <div className="flex items-center space-x-1">
              <EyeIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{idea.viewCount || 0} </span>
            </div>
            <div className="flex items-center space-x-1">
              <ChatIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{idea.commentCount || 0} </span>
            </div>
          </div>

          {/* View Details Link */}
          <Link
            to={`/idea/${idea._id}`}
            onClick={markAsRead}
            className={`text-sm ${!isRead ? 'text-brand-700 font-bold' : 'text-brand-600'} hover:text-brand-800`}
          >
            {!isRead ? 'New Idea' : 'View Details'}
          </Link>
        </div>

        {/* Creation Date */}
        {idea.createdAt && (
          <div className={`text-xs mt-2 ${!isRead ? 'text-brand-500' : 'text-gray-500'}`}>
            Posted on {formatDate(idea.createdAt)}
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('recent');

  useEffect(() => {
    const loadIdeas = async () => {
      try {
        setIsLoading(true);
        const fetchedIdeas = await fetchIdeas({
          sort: filter,
          page: 1,
          limit: 10
        });
        setIdeas(fetchedIdeas);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadIdeas();
  }, [filter]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-red-500">
        <p>Error loading ideas: {error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 bg-brand-600 text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Knivok Innovators Dashboard
          </h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">
              Welcome, {user?.name || 'Innovator'}
            </span>
            <button 
              onClick={logout}
              className="text-red-500 hover:text-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Action Buttons and Filters */}
          <div className="mb-6 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-semibold text-gray-800">
                All Ideas
              </h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="ml-4 form-select block w-full sm:w-auto py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              >
                <option value="recent">Most Recent</option>
                <option value="most_upvoted">Top Voted</option>
                <option value="most_commented">Most Commented</option>
              </select>
            </div>
            <Link 
              to="/create-idea"
              className="inline-flex items-center px-4 py-2 border border-transparent 
              text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 
              hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
              focus:ring-brand-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Idea
            </Link>
          </div>

          {/* Ideas Grid */}
          {ideas.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <ViewGridIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-500">
                No ideas yet. Be the first to share an innovation!
              </p>
              <Link
                to="/create-idea"
                className="mt-4 inline-block bg-brand-600 text-white px-4 py-2 rounded hover:bg-brand-700"
              >
                Create Your First Idea
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ideas.map((idea) => (
                <IdeaCard key={idea._id} idea={idea} />
              ))}
            </div>
          )}

          {/* Pagination (if needed) */}
          {ideas.length > 0 && (
            <div className="mt-8 flex justify-center">
              <nav aria-label="Pagination" className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  1 of 5
                </span>
                <button
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
